import safeStorage, { safeSessionStorage } from './localStorage';

/**
 * Returns the origin + pathname of document.referrer (e.g. "https://partner-site.com/register"),
 * stripping all query params to avoid leaking Firebase/auth tokens into downstream systems.
 * Falls back to window.location.origin when the referrer is empty or unparseable.
 */
export const getReferrerOrigin = () => {
  try {
    const { origin, pathname } = new URL(document.referrer);
    return `${origin}${pathname}`;
  } catch {
    return `${window.location.origin}${window.location.pathname}`;
  }
};

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
];

export const CLICK_ID_KEYS = [
  'gclid', // Google Ads
  'gbraid', // Google Ads (iOS)
  'wbraid', // Google Ads (web-to-app)
  'fbclid', // Meta / Facebook
  'msclkid', // Microsoft Ads
  'ttclid', // TikTok Ads
  'irclickid', // Impact.com
];

// GLOBAL_KEY matches Webflow + Shopify so all three domains share one store.
// SESSION_KEY is the legacy React app key — kept for backwards compat reads.
const GLOBAL_KEY = 'utm_params_global';
const SESSION_KEY = 'utms';
const CLICK_IDS_KEY = 'click_ids';

// FIRST_TOUCH_KEY is deliberately distinct from GLOBAL_KEY: that one holds LAST
// touch and is overwritten on every new campaign arrival.
const FIRST_TOUCH_KEY = 'attribution_first';

const COOKIE_DOMAIN = '.trashie.io';
const COOKIE_MAX_AGE = 86400; // 24 hours — last touch
const FIRST_TOUCH_MAX_AGE = 7776000; // 90 days — matches Meta's attribution window

/**
 * Read UTM cookie shared across all .trashie.io subdomains.
 * Handles both flat format { utm_source: "x", _ts: 123 }
 * and nested format { params: { utm_source: "x" }, _ts: 123 }.
 */
const readUtmCookie = (key, maxAgeMs = COOKIE_MAX_AGE * 1000) => {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${key}=([^;]+)`),
    );
    if (!match) return null;
    const raw = JSON.parse(decodeURIComponent(match[1]));
    // Expire once older than the caller's window (24h for last touch, 90d for first)
    if (raw._ts && Date.now() - raw._ts > maxAgeMs) return null;
    // Nested format (some older Shopify writes)
    if (raw.params) return raw.params;
    // Flat format — strip internal _ts key
    const { _ts, ...rest } = raw;
    return Object.keys(rest).length > 0 ? rest : null;
  } catch {
    return null;
  }
};

/**
 * Write UTM cookie on .trashie.io so all subdomains can read it.
 */
const writeUtmCookie = (key, obj, maxAge = COOKIE_MAX_AGE) => {
  try {
    const val = encodeURIComponent(JSON.stringify({ ...obj, _ts: Date.now() }));
    document.cookie = `${key}=${val}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {
    /* silent */
  }
};

/**
 * First-touch attribution: the campaign a visitor ARRIVED on.
 *
 * `utm_params_global` holds LAST touch — captureUtms overwrites it on every new
 * campaign arrival. That silently rewrites the acquisition source for anyone who
 * returns through a different campaign, a remarketing ad, or an internal link
 * carrying a stray parameter. Measured against PostHog for Aug 2026, first and
 * last touch disagree for 15.5% of visitors who have a first-touch source at all.
 *
 * Written once and never overwritten, and kept for 90 days rather than the 24h
 * the last-touch cookie uses, so it outlives Meta's attribution window.
 */
const readFirstTouchLocal = () => {
  try {
    const raw = safeStorage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed._ts && Date.now() - parsed._ts > FIRST_TOUCH_MAX_AGE * 1000) return null;
    const { _ts, ...rest } = parsed;
    return Object.keys(rest).length > 0 ? rest : null;
  } catch {
    return null;
  }
};

const captureFirstTouch = (hit) => {
  try {
    // Write-once: bail if either store already holds a live first touch.
    if (readFirstTouchLocal()) return;
    if (readUtmCookie(FIRST_TOUCH_KEY, FIRST_TOUCH_MAX_AGE * 1000)) return;

    // Path only, never the query string. This value is persisted for 90 days
    // and forwarded to Shopify as a cart attribute, where it is visible on the
    // order — and a campaign landing URL routinely carries `oobCode`, `token`
    // or `email` on top of the UTMs. Same reasoning as getReferrerOrigin above,
    // which strips params for exactly this reason. The campaign itself is
    // already captured in `hit`, so nothing attributional is lost.
    const firstTouch = {
      ...hit,
      landing_page: window.location.pathname,
    };

    // `_ts` is stored in BOTH copies so the 90-day window applies to whichever
    // one is read. Without it the localStorage copy — which is read first —
    // never expires, so the two stores disagree: permanent with localStorage
    // intact, rolling over at 90 days without it.
    safeStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({ ...firstTouch, _ts: Date.now() }));
    writeUtmCookie(FIRST_TOUCH_KEY, firstTouch, FIRST_TOUCH_MAX_AGE);
  } catch {
    /* best-effort: never let first-touch capture break last-touch capture */
  }
};

/**
 * Returns the stored first-touch attribution, or {} when none was captured.
 * Priority mirrors getStoredUtms: localStorage first, then the shared cookie.
 */
export const getFirstTouchAttribution = () => {
  try {
    const local = readFirstTouchLocal();
    if (local) return local;

    const cookie = readUtmCookie(FIRST_TOUCH_KEY, FIRST_TOUCH_MAX_AGE * 1000);
    if (cookie) {
      safeStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(cookie));
      return cookie;
    }

    return {};
  } catch {
    return {};
  }
};

/**
 * Returns stored UTMs + click IDs as a single flat object.
 * Priority: sessionStorage → cookie → utm_params_global (localStorage) → utms (legacy).
 */
export const getStoredUtms = () => {
  try {
    // 1. sessionStorage — fastest, set by captureUtms on this tab
    const ss = safeSessionStorage.getItem(SESSION_KEY);
    if (ss) return JSON.parse(ss);

    // 2. Cross-domain COOKIE (shared by Webflow, Shopify, and this app)
    const cookieUtms = readUtmCookie(GLOBAL_KEY);
    if (cookieUtms) {
      safeSessionStorage.setItem(SESSION_KEY, JSON.stringify(cookieUtms));
      return cookieUtms;
    }

    // 3. Unified cross-domain localStorage key (fallback)
    const global = safeStorage.getItem(GLOBAL_KEY);
    if (global) {
      safeSessionStorage.setItem(SESSION_KEY, global);
      return JSON.parse(global);
    }

    // 4. Legacy React-only localStorage key (pre-fix sessions)
    const legacy = safeStorage.getItem(SESSION_KEY);
    if (legacy) {
      safeSessionStorage.setItem(SESSION_KEY, legacy);
      return JSON.parse(legacy);
    }

    return {};
  } catch {
    return {};
  }
};

/**
 * Click IDs are now merged into utm_params_global along with UTMs.
 * This reads them back out for callers that need them separately.
 */
export const getStoredClickIds = () => {
  try {
    const all = getStoredUtms();
    const ids = {};
    CLICK_ID_KEYS.forEach((k) => { if (all[k]) ids[k] = all[k]; });
    return ids;
  } catch {
    return {};
  }
};

/**
 * Called once on app mount by UtmProvider.
 * Reads UTMs + click IDs from the landing URL, merges with anything
 * already stored (URL params win), and writes to BOTH storage keys
 * so Shopify and Webflow can read utm_params_global.
 */
export const captureUtms = () => {
  const params = new URLSearchParams(window.location.search);
  const urlUtms = {};
  const urlClickIds = {};

  UTM_KEYS.forEach((key) => {
    const val = params.get(key);
    if (val) urlUtms[key] = val;
  });

  CLICK_ID_KEYS.forEach((key) => {
    const val = params.get(key);
    if (val) urlClickIds[key] = val;
  });

  const hasNewUtms = Object.keys(urlUtms).length > 0;
  const hasNewClickIds = Object.keys(urlClickIds).length > 0;
  const hasAnything = hasNewUtms || hasNewClickIds;

  if (hasAnything) {
    const merged = { ...urlUtms, ...urlClickIds };
    const serialized = JSON.stringify(merged);

    // First touch is written before the last-touch writes below, so the very
    // first campaign a visitor arrives on is preserved even though `merged`
    // goes on to overwrite utm_params_global.
    captureFirstTouch(merged);

    // Write to unified cross-domain key (Webflow + Shopify read this)
    safeStorage.setItem(GLOBAL_KEY, serialized);

    // Write to cross-domain cookie (Webflow + Shopify read this)
    writeUtmCookie(GLOBAL_KEY, merged);

    // Write to sessionStorage for fast in-tab reads
    safeSessionStorage.setItem(SESSION_KEY, serialized);

    // Keep legacy click_ids key for any existing callers
    if (hasNewClickIds) {
      const clickSerialized = JSON.stringify(urlClickIds);
      safeSessionStorage.setItem(CLICK_IDS_KEY, clickSerialized);
      safeStorage.setItem(CLICK_IDS_KEY, clickSerialized);
    }

    return merged;
  }

  // Nothing in URL — fall back to whatever is stored.
  // This is the cross-domain inheritance path: user lands on app.trashie.io
  // from shop.trashie.io with no UTMs in URL, we read utm_params_global
  // that Shopify wrote and use those.
  const inherited = getStoredUtms();

  // Seed first touch from the inherited campaign too. Only this app writes
  // `attribution_first`, so a visitor actually acquired on shop.trashie.io or
  // the Webflow site — who then reaches app.trashie.io with no params — would
  // otherwise have no first touch at all. The next campaign they arrive on
  // (remarketing, newsletter) would then be recorded as the first, which is
  // precisely the miscredit this store exists to prevent.
  if (Object.keys(inherited).length) {
    captureFirstTouch(inherited);
  }

  return inherited;
};

// Hosts we own. Decoration is limited to these because the params carried
// include click IDs (fbclid, gclid, ttclid, irclickid) — appending those to a
// third-party URL hands an advertising identifier to a site we do not control,
// and it would show up in their logs and referrer chains.
const isOwnedHost = (hostname) => hostname === 'trashie.io'
  || hostname.endsWith('.trashie.io')
  || hostname.endsWith('.myshopify.com');

/**
 * Appends stored UTMs + click IDs to a first-party URL.
 * Does not overwrite params already present in the target URL.
 * Third-party URLs are returned untouched — see isOwnedHost.
 */
export const appendUtmParams = (url) => {
  if (!url || url === '#' || url.startsWith('#')) return url;

  try {
    const stored = getStoredUtms();
    if (!Object.keys(stored).length) return url;

    const parsed = new URL(url, window.location.origin);

    // Relative URLs resolve against our own origin, so they are always ours.
    // Anything that resolves elsewhere must be on the allowlist.
    if (!isOwnedHost(parsed.hostname)) return url;

    // Append UTMs
    UTM_KEYS.forEach((key) => {
      if (stored[key] && !parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, stored[key]);
      }
    });

    // Append click IDs so they survive the hop
    CLICK_ID_KEYS.forEach((key) => {
      if (stored[key] && !parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, stored[key]);
      }
    });

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return parsed.toString();
    }
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return url;
  }
};
